import cx from 'classnames';
import { useRouter } from 'next/router';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useInstantSearch, useRefinementList } from 'react-instantsearch';

import { Container, Icon, ScrollArrows, Text } from 'components/@tedui';

import { useTrackClickEvent } from '../../analytics';
import { useTopicsModalContext } from '../../Modals';

import { useTopicsLaunchPadLogic } from './hooks';
import { getTitleInfo, Title } from './Title';
import type { TopicsLaunchPadProps } from './TopicsLaunchPad.props';
import { TopicsRow } from './TopicsRow';
import { transformFeaturedTopics } from './utils';

function TopicsLaunchPad({
  page,
  position = 'Bottom',
  isOnResultsPage
}: TopicsLaunchPadProps) {
  const router = useRouter();
  const ISTALKSPAGE = router.pathname === '/talks';

  const { openModal } = useTopicsModalContext();
  const { status, results } = useInstantSearch();
  const { items, refine } = useRefinementList({
    attribute: 'tags',
    limit: 500,
    transformItems: transformFeaturedTopics
  });

  const firstRowTopics = items.slice(0, 7);
  const secondRowTopics = items.slice(7);

  const [showArrows, setShowArrows] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const trackClick = useTrackClickEvent(
    results
      ? {
          index: results.index,
          nbHits: results.nbHits
        }
      : undefined
  );

  const classNames = {
    container: cx('flex max-w-tui-content flex-col text-center', {
      'pt-8 sm-tui:hidden lg-tui:pt-10': page === 'Home',
      'pt-2 md-tui:pt-3 lg-tui:pt-4 2xl-tui:pt-6': page !== 'Home',
      'order-last': page === 'Results',
      'mb-16 md-tui:mb-12 lg-tui:mb-20 2xl-tui:mb-28':
        page === 'Results' || (page === 'Default' && position === 'Bottom'),
      'pointer-events-none opacity-50': status === 'error'
    }),
    scrollArea: 'overflow-auto scroll-smooth scrollbar-hide',
    titleWrapper: 'pb-2 md-tui:pb-4',
    topicsRow: cx('flex w-full gap-4', {
      'justify-center': !showArrows
    }),
    buttonWrapper: 'mt-5 flex flex-col items-center md-tui:mt-6 2xl-tui:mt-8',
    button: 'flex items-center gap-1 border-b-thin border-black'
  };

  const { handleOnSelect } = useTopicsLaunchPadLogic({
    page,
    position,
    refine
  });

  useEffect(() => {
    if (!scrollAreaRef.current) return;
    const checkOverflow = () => {
      if (scrollAreaRef.current) {
        setShowArrows(
          scrollAreaRef.current.offsetWidth < scrollAreaRef.current.scrollWidth
        );
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [items]);

  const handleSeeAllClick = () => {
    openModal();
    trackClick({
      eventName: 'launch_topic_modal',
      additionalEvents: {
        event_interaction_context: 'see_all_link'
      }
    });
  };

  const titleInfo = useMemo(() => {
    const { titleText, hasLargeText } = getTitleInfo(page, position);
    return { title: titleText, hasLargeText };
  }, [page, position]);

  if (isOnResultsPage && page === 'Default') return null;

  return (
    <Container
      className={classNames.container}
      id={`TopicsLaunchPad-${position}-${page}`}
      testId="topicsLaunchPad"
    >
      <Title text={titleInfo.title} hasLargeText={titleInfo.hasLargeText} />

      <div className={classNames.scrollArea} ref={scrollAreaRef}>
        <TopicsRow
          className={cx(classNames.topicsRow, 'pb-2')}
          topics={firstRowTopics}
          onSelect={handleOnSelect}
        />

        <ScrollArrows
          scrollAreaRef={scrollAreaRef}
          showArrows={showArrows}
          {...(!ISTALKSPAGE && {
            gradientColor: 'white'
          })}
        />

        <TopicsRow
          className={cx(classNames.topicsRow, 'pt-2')}
          topics={secondRowTopics}
          onSelect={handleOnSelect}
        />
      </div>

      {((page === 'Default' && position === 'Top') || page === 'Home') && (
        <div className={classNames.buttonWrapper}>
          <button
            type="button"
            className={classNames.button}
            onClick={handleSeeAllClick}
            tabIndex={-1}
          >
            <Text tag="p" variant="body2">
              See all
            </Text>
            <Icon iconName="arrow-right" />
          </button>
        </div>
      )}
    </Container>
  );
}

export default memo(TopicsLaunchPad);
